import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Box,
  Container,
  LinearProgress,
  Link,
  Paper,
  Typography,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Link as RouterLink, useLocation } from "react-router-dom";
import moment from "moment";
import currency from "currency.js";
import bkstApi from "../../api";
import {
  sortByOrderDateTime,
  equalsIgnoreCase,
  getCustomerName,
  anyWordStartsWith,
  isPOSMode,
  getStoreFilterStreets,
  orderMatchesStoreFilter,
} from "../../common/util";
import ItemFilter from "../../planner/component/ItemFilter";
import SearchIcon from "@mui/icons-material/Search";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import NothingFound from "../../common/search/NothingFound";
import ActiveOrderRow from "../component/ActiveOrderRow";
import PmCodeIcon from "../../order/component/PmCodeIcon";
import { pmCodeLabel } from "../../order/component/pmCodeLabel";
import CardBrand from "../../order/component/CardBrand";
import UserAvatar from "../../common/UserAvatar";
import { UserContext } from "../../context/UserContext";

export default function Today(props) {
  let location = useLocation();
  const { user } = useContext(UserContext);

  const [tab, setTab] = useState("due");

  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("");

  const [dueOrders, setDueOrders] = useState([]);
  const [filteredDueOrders, setFilteredDueOrders] = useState([]);
  const storeLocations = user?.config?.storeLocation || [];
  const storeStreets = getStoreFilterStreets(user);
  const showStoreFilter = storeStreets.length > 1;
  const [transactions, setTransactions] = useState([]);

  const [loading, setLoading] = useState(true);
  const posMode = isPOSMode(user);

  useEffect(() => {
    setLoading(true);
    bkstApi.get(`/my/today`).then((res) => {
      setDueOrders(res.data.orders.upcoming.sort(sortByOrderDateTime));
      setTransactions(res.data.transactions || []);

      setLoading(false);
    });
  }, [location.key]);

  useEffect(() => {
    let arr = [];
    arr = dueOrders.filter((o) => {
      // check filters
      for (const [k, v] of Object.entries(filter)) {
        if (v.value) {
          if (k === "fulfillment" && !equalsIgnoreCase(o.fulfillment.type, v.value)) {
            return false;
          } else if (k === "store") {
            if (!orderMatchesStoreFilter(o, v.value, storeLocations)) {
              return false;
            }
          }
        }
      }
      // passed all filters
      return true;
    });
    // Apply text query filter
    if (query) {
      arr = arr.filter(
        (o) =>
          anyWordStartsWith(getCustomerName(o.customer), query) ||
          o.customer.phone.slice(-4).startsWith(query)
      );
    }
    setFilteredDueOrders(arr);
  }, [filter, query, dueOrders]);

  const rows = useMemo(
    () =>
      transactions.map((tx) => ({
        ...tx,
        gridId: `${tx.orderId}_${tx.id}`,
      })),
    [transactions]
  );

  const columns = useMemo(
    () => [
      {
        field: "ts",
        headerName: "Date",
        width: 100,
        valueGetter: (params) => Number(params.row.ts) || 0,
        renderCell: (params) =>
          params.row.ts ? (
            <Box sx={{ py: 0.5, lineHeight: 1.4 }}>
              <Typography variant="body2" display="block">
                {moment(Number(params.row.ts)).format("h:mm A")}
              </Typography>
            </Box>
          ) : (
            "—"
          ),
      },
      {
        field: "amount",
        headerName: "Amount",
        width: 100,
        align: "right",
        headerAlign: "right",
        valueGetter: (params) => Number(params.row.amount) || 0,
        renderCell: (params) => (
          <Typography variant="body2">{currency(params.row.amount || 0).format()}</Typography>
        ),
      },
      {
        field: "pmCode",
        headerName: "Method",
        flex: 1,
        minWidth: 150,
        renderCell: (params) => (
          <Box>
            <Box display="flex" alignItems="center" gap={0.75}>
              <PmCodeIcon pmCode={params.row.pmCode} fontSize="small" color="action" />
              <Typography variant="body2">{pmCodeLabel(params.row.pmCode)}</Typography>
            </Box>
            {params.row.reference && (
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{ pl: 3.25 }}
              >
                {params.row.reference}
              </Typography>
            )}
          </Box>
        ),
      },
      {
        field: "card",
        headerName: "Card",
        width: 140,
        sortable: false,
        renderCell: (params) => <CardBrand brand={params.row.brand} last4={params.row.last4} />,
      },
      {
        field: "uid",
        headerName: "Staff",
        width: 72,
        sortable: false,
        renderCell: (params) => (params.row.uid ? <UserAvatar uid={params.row.uid} /> : "—"),
      },
      ...(posMode
        ? []
        : [
            {
              field: "dcid",
              headerName: "POS",
              width: 72,
              sortable: false,
              renderCell: (params) =>
                params.row.dcid ? <UserAvatar uid={params.row.dcid} /> : "—",
            },
          ]),
      {
        field: "orderId",
        headerName: "Order",
        width: 110,
        renderCell: (params) =>
          params.row.orderId ? (
            <Link component={RouterLink} to={`/order/${params.row.orderId}`} underline="hover">
              View
            </Link>
          ) : (
            "—"
          ),
      },
    ],
    [posMode]
  );

  if (loading) {
    return <LinearProgress />;
  } else {
    return (
      <Container maxWidth={"lg"}>
        <Box mt={4} mb={2}>
          <Box>
            <Box
              mb={2}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                maxWidth: "90vw",
              }}
            >
              <Box>
                <Typography variant="h1" gutterBottom>
                  Today
                </Typography>
                <Typography variant="subtitle1" color="textSecondary">
                  Open orders to fulfill and payments taken today
                </Typography>
              </Box>
            </Box>
            <Box sx={{ maxWidth: "95vw" }}>
              <Tabs
                value={tab}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{ borderBottom: "1px solid #ECECEC" }}
              >
                <Tab
                  value="due"
                  label={`Open Orders (${dueOrders.length})`}
                  onClick={() => setTab("due")}
                />
                <Tab
                  value="new"
                  label={`Transactions (${transactions.length})`}
                  onClick={() => setTab("new")}
                />
              </Tabs>
              <Box my={2}>{alert}</Box>
            </Box>
            {tab === "new" && (
              <Box>
                <Box my={3}>
                  <Typography variant="body1" color="textSecondary">
                    {posMode
                      ? "Transactions today on this POS"
                      : "Transactions today across all channels"}
                  </Typography>
                </Box>
                {transactions.length === 0 && <NothingFound />}
                {transactions.length > 0 && (
                  <Box>
                    <Box sx={{ height: 620, width: "100%", mt: 3 }}>
                      <DataGrid
                        rows={rows}
                        columns={columns}
                        getRowId={(row) => row.gridId}
                        disableRowSelectionOnClick
                        disableColumnMenu
                        rowHeight={56}
                        initialState={{
                          sorting: { sortModel: [{ field: "ts", sort: "desc" }] },
                        }}
                        sx={{
                          "& .MuiDataGrid-cell": {
                            borderBottom: "1px solid #ECECEC",
                          },
                          "& .MuiDataGrid-columnHeaders": {
                            backgroundColor: "#ECECEC",
                          },
                          "& .MuiDataGrid-row:nth-of-type(odd)": {
                            backgroundColor: "#FAFAFA",
                          },
                          "& .MuiDataGrid-row:nth-of-type(even)": {
                            backgroundColor: "#FFF",
                          },
                          "& .MuiDataGrid-cell:focus": {
                            outline: "none",
                          },
                          "& .MuiDataGrid-cell:focus-within": {
                            outline: "none",
                          },
                        }}
                      />
                    </Box>
                  </Box>
                )}
              </Box>
            )}
            {tab === "due" && (
              <Box>
                <Box my={3}>
                  <Typography variant="body1" color="textSecondary">
                    Orders due today that haven't been fulfilled yet
                  </Typography>
                </Box>
                {dueOrders.length === 0 && <NothingFound />}
                {dueOrders.length > 0 && (
                  <Box my={2}>
                    <Paper elevation={0}>
                      <Box
                        py={0.75}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginRight: "5px",
                        }}
                      >
                        <ItemFilter
                          config={[
                            {
                              name: "fulfillment",
                              options: dueOrders.map((o) => o.fulfillment?.type),
                            },
                            showStoreFilter && !posMode
                              ? { name: "store", options: storeStreets }
                              : "",
                          ]}
                          data={dueOrders}
                          onChange={(o) => setFilter({ ...filter, ...o })}
                        />
                        <TextField
                          id="query"
                          value={query}
                          label=""
                          name="query"
                          variant="outlined"
                          size="small"
                          onChange={(e) => setQuery(e.target.value)}
                          placeholder="Name or Phone (last 4)"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment sx={{ paddingRight: "10px" }}>
                                <SearchIcon />
                              </InputAdornment>
                            ),
                            endAdornment: (
                              <InputAdornment>
                                {query && (
                                  <IconButton onClick={(e) => setQuery("")}>
                                    <CloseOutlinedIcon />
                                  </IconButton>
                                )}
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "25px", // Adjust the value for desired roundness
                              "& fieldset": {
                                borderColor: "primary.secondary",
                              },
                              "&:hover fieldset": {
                                borderColor: "primary.main",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "primary.main",
                              },
                            },
                          }}
                        />
                      </Box>
                    </Paper>
                  </Box>
                )}
                {filteredDueOrders.map((o) => (
                  <ActiveOrderRow key={o.id} order={o} />
                ))}
              </Box>
            )}
          </Box>
        </Box>
      </Container>
    );
  }
}
